/**
 * <collapsible-content>
 * Caps its content at `data-collapsed-height` and toggles the full height with a
 * read more / read less button. Only shows the toggle when the content overflows.
 *
 * Being a custom element, it re-initialises itself whenever the theme editor
 * re-renders the section, and cleans up in disconnectedCallback.
 */
if (!customElements.get('collapsible-content')) {
  class CollapsibleContent extends HTMLElement {
    // Don't collapse content that only overflows by a line or so
    static OVERFLOW_THRESHOLD = 40;

    connectedCallback() {
      this.viewport = this.querySelector('[data-collapsible-viewport]');
      this.body = this.querySelector('[data-collapsible-body]');
      this.button = this.querySelector('[data-collapsible-toggle]');
      this.label = this.querySelector('[data-collapsible-label]');
      if (!this.viewport || !this.body || !this.button) return;

      this.collapsedHeight = parseInt(this.dataset.collapsedHeight, 10) || 200;
      this.expanded = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onToggle = this.toggle.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.button.addEventListener('click', this.onToggle);
      this.viewport.addEventListener('transitionend', this.onTransitionEnd);

      // Re-check overflow as images, fonts and viewport width change the content height
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.body);
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.button?.removeEventListener('click', this.onToggle);
      this.viewport?.removeEventListener('transitionend', this.onTransitionEnd);
      clearTimeout(this.fallbackTimer);
    }

    update() {
      const overflowing = this.body.offsetHeight > this.collapsedHeight + CollapsibleContent.OVERFLOW_THRESHOLD;
      this.classList.toggle('is-overflowing', overflowing);
      this.classList.toggle('is-static', !overflowing);
      this.button.hidden = !overflowing;

      if (!overflowing && this.expanded) {
        this.expanded = false;
        this.classList.remove('is-expanded');
        this.viewport.style.maxHeight = '';
        this.syncButton();
      }
    }

    toggle() {
      this.expanded ? this.collapse() : this.expand();
    }

    expand() {
      this.expanded = true;
      this.animateTo(this.body.offsetHeight);
      this.classList.add('is-expanded');
      this.syncButton();
    }

    collapse() {
      this.expanded = false;
      const distance = this.animateTo(this.collapsedHeight);
      this.classList.remove('is-expanded');
      this.syncButton();

      // If the top of the description has scrolled off screen, bring it back
      // alongside the collapse so the reader isn't left stranded further down the page
      const top = this.getBoundingClientRect().top;
      if (top < 0 && distance > 0) {
        this.scrollIntoView({
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start',
        });
      }
    }

    // Animates max-height from its current rendered height (so a toggle mid-animation
    // reverses smoothly) to the target. Returns the distance travelled.
    animateTo(target) {
      clearTimeout(this.fallbackTimer);
      const start = this.viewport.offsetHeight;
      const distance = Math.abs(target - start);

      if (this.reducedMotion.matches || distance === 0) {
        this.finish();
        return distance;
      }

      // Longer text gets a slightly longer, but still snappy, animation
      const duration = Math.round(Math.min(700, Math.max(300, 250 + distance * 0.4)));
      this.style.setProperty('--collapsible-duration', `${duration}ms`);

      this.viewport.style.maxHeight = `${start}px`;
      this.viewport.offsetHeight; // force reflow so the start height is committed
      this.viewport.style.maxHeight = `${target}px`;

      // transitionend doesn't fire if the transition is interrupted or skipped
      this.fallbackTimer = setTimeout(() => this.finish(), duration + 50);
      return distance;
    }

    onTransitionEnd(event) {
      if (event.target !== this.viewport || event.propertyName !== 'max-height') return;
      clearTimeout(this.fallbackTimer);
      this.finish();
    }

    // Hand control back to the stylesheet: `none` when expanded so the content can
    // reflow freely, and the CSS cap when collapsed
    finish() {
      this.viewport.style.maxHeight = this.expanded ? 'none' : '';
    }

    syncButton() {
      this.button.setAttribute('aria-expanded', String(this.expanded));
      if (this.label) {
        this.label.textContent = this.expanded ? this.button.dataset.labelLess : this.button.dataset.labelMore;
      }
    }
  }

  customElements.define('collapsible-content', CollapsibleContent);
}
